import { useQuery } from '@apollo/client';
import { Delete } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { Link } from '~/components/link';
import { ContentCardActions, ContentCardThumbnail, GridCard } from '~/components/media/card';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { useLocation } from '~/hooks/location';
import type { MediaSelect__MediaItem as MediaItem } from '../index.generated';
import { MediaPreviewDocument } from '../index.generated';

// Links to the item's details when the user can edit it. Only the thumbnail links, so the
// remove button beside the title is not nested inside the link.
const PreviewThumbnail = ({ mediaItem }: { mediaItem: MediaItem }) => {
  const link = useLink();
  const location = useLocation();

  if (!mediaItem.canUpdate.value) return <ContentCardThumbnail item={mediaItem} />;

  return (
    <Link
      to={link(`/content/${mediaItem.contentFolderId}/media/${mediaItem.id}/details`)}
      state={{ returnTo: location.fullPath }}
      sx={{ display: 'block' }}
    >
      <ContentCardThumbnail item={mediaItem} />
    </Link>
  );
};

interface MediaPreviewProps {
  onRemove?: (mediaItemId: number) => void;
  mediaItemIds: number[];
  small?: boolean;
}

export const MediaPreview = ({ mediaItemIds, onRemove, small = false }: MediaPreviewProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data } = useQuery(MediaPreviewDocument, {
    variables: { mediaItemIds, networkId: currentNetwork.id },
  });

  const mediaItems = data?.network?.mediaItems;

  if (!mediaItems || mediaItems.length === 0) return null;

  return (
    <Grid container spacing={1} sx={{ justifyContent: 'flex-end' }}>
      {mediaItems.map((mediaItem) => (
        <Grid item key={mediaItem.id} xs={small ? 6 : 12}>
          <GridCard>
            <PreviewThumbnail mediaItem={mediaItem} />
            <ContentCardActions item={mediaItem} small={small}>
              {onRemove && (
                <IconButton
                  label={t('common.actions.remove')}
                  onClick={() => onRemove(mediaItem.id)}
                  size="small"
                  sx={{ color: 'error.main', mr: 0.5 }}
                >
                  <Delete />
                </IconButton>
              )}
            </ContentCardActions>
          </GridCard>
        </Grid>
      ))}
    </Grid>
  );
};
